import axios from 'axios'
import qs from 'qs'
import { ElMessage } from 'element-plus'
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import { clearStorage, getCompanyId, getToken, setToken } from '@/utils/auth'
import { getOauthTokenApi } from '@/api/auth'
import router from '@/router'

const pendingMap = new Map<string, AbortController>()

class DuplicateRequestError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DuplicateRequestError'
  }
}
// 文件上传采用FormData格式，无法确定文件内容是否有变化，文件下载的请求不需要防重复提交
function isFileUpload(config: AxiosRequestConfig): boolean {
  const { data, responseType } = config || {}
  // Check if it's a FormData with files
  if (data instanceof FormData) {
    for (const [key, value] of data.entries()) {
      if (value instanceof File || value instanceof Blob) {
        return true
      }
    }
  }

  // Check if file download (based on responseType)
  if (['arraybuffer', 'blob'].includes((responseType || '').toLowerCase())) {
    return true
  }

  return false
}

function generateReqKey(config: AxiosRequestConfig) {
  const { method, url, data } = config || {}
   if (['post', 'delete'].includes((method || '').toLowerCase()) && !isFileUpload(config) && !config.skipDuplicateCheck) {
    let normalizedData = data
    // 如果是字符串，尝试解析为对象
    if (typeof data === 'string') {
      try {
        normalizedData = JSON.parse(data)
      } catch (e) {
        normalizedData = data
      }
    }
    // 最后统一转换为字符串
    return [method, url, JSON.stringify(normalizedData)].join('&')
  }
  return ''
}
// Add function to handle pending requests
function addPending(config: AxiosRequestConfig) {
  const requestKey = generateReqKey(config)
  // 如果不是 POST 请求或没有 key，直接返回
  if (!requestKey) return false

  // 如果已经有相同的请求在进行中，返回 true 表示重复
  if (pendingMap.has(requestKey)) {
    // const controller = pendingMap.get(requestKey)
    // controller?.abort()
    return true
  }

  const controller = new AbortController()
  config.signal = controller.signal
  pendingMap.set(requestKey, controller)
  return false
}

// Add function to remove pending request
function removePending(config: AxiosRequestConfig) {
  const requestKey = generateReqKey(config)
  if (requestKey && pendingMap.has(requestKey))
    pendingMap.delete(requestKey)
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    noToast?: boolean
    skipDuplicateCheck?: boolean
  }
}

export interface HttpRequestConfig extends AxiosRequestConfig {
  unique: boolean;
}

export interface Result<T = any> {
  code: string | number;
  message: string;
  data: T;
  daily?: any;
  location?: any;
}

export class Request {
  instance: AxiosInstance
  baseConfig: AxiosRequestConfig = {
    baseURL: import.meta.env.DEV ? '/api' : '',
    paramsSerializer: {
      serialize: (params: { [key: string]: any }) => {
        return qs.stringify(params)
      },
    },
  }

  constructor(isFile = false, config: AxiosRequestConfig = {}) {
    this.instance = axios.create(Object.assign(this.baseConfig, config))
    this.requestInterceptors()
    isFile ? this.responseFileInterceptors() : this.responseInterceptors()
  }

  // 请求拦截
  private requestInterceptors(): void {
    this.instance.interceptors.request.use(
      (config) => {
        if (addPending(config))
          return Promise.reject(new DuplicateRequestError('请求正在进行中，请勿重复提交'))

        config.headers = config.headers || {}
        config.headers.serviceType = SERVICE_TYPE

        const data = getToken()

        if (data) {
          config.headers.Authorization = `Bearer ${data.access_token}`
          config.headers.companyId = getCompanyId()
        }
        if (config.method === 'get' && config.url)
          config.url += `${config.url.indexOf('?') > -1 ? '&' : '?'}_t=${Date.now()}`

        return config
      },
      (error: AxiosError) => {
        return Promise.reject(error)
      },
    )
  }

  // 响应拦截
  private responseInterceptors() {
    this.instance.interceptors.response.use(
      (response: AxiosResponse): typeof response.data => {
        removePending(response.config)
        const { data, message, code }: Result = response.data
        if (code === '00000') return data
        if (code === '20003') return this.refreshToken(response) // 登录过期
        let customMessage: string
        switch (code) {
          case '20004': // 刷新token时，refreshToken失效
            clearStorage()
            router.push({ name: 'PasswordLogin' })
            customMessage = '登录过期，请重新登录'
            break
          // case '20003':
          //   console.log('20003 response', response)
          //   this.refreshToken(response)
          //   break
          case '99204': // 验证平台权限
            customMessage = message
            break
          case '10000':
            customMessage = '系统运行异常，请稍后重试'
            break
          default:
            customMessage = message
        }
        if (response.config.noToast !== true) {
          ElMessage({
            message: customMessage,
            grouping: true,
            type: 'error',
          })
        }
        // ElMessage.error(customMessage)
        return Promise.reject(response)
      },
      (error: AxiosError) => {
        // removePending(error.config!)
        if (error instanceof DuplicateRequestError) {
          console.log('DuplicateRequestError error', error)
          return Promise.reject(error)
        }
        return this.handleError(error)
      },
    )
  }

  // 导出文件响应拦截
  private responseFileInterceptors() {
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        removePending(response.config)
        return response
      },
      (error: AxiosError) => {
        if (error instanceof DuplicateRequestError) {
          console.log('DuplicateRequestError error', error)
          return Promise.reject(error)
        }
        this.handleError(error)
      },
    )
  }

  private handleError(error: AxiosError) {
    removePending(error.config!)
    let message: string
    const { status } = error.response!
    switch (status) {
      case 500:
        message = '服务器连接失败'
        break
      case 401:
        message = '登录过期，请重新登录'
        break
      default:
        message = `系统异常，请刷新页面或检查网络连接(错误码：${status})`
    }
    ElMessage.error(message)
    return Promise.reject(new Error(message))
  }

  // 刷新token
  private refreshToken(response: AxiosResponse) {
    // 获取refreshToken
    const refreshToken = getToken()?.refresh_token

    function reLogin() {
      clearStorage()
      router.push({ name: 'PasswordLogin' })
      ElMessage({
        message: '登录过期，请重新登录',
        grouping: true,
        type: 'error',
      })
      return Promise.reject(response)
    }

    if (!refreshToken)
      return reLogin()

    return getOauthTokenApi({
      client_id: 'client_super_admin',
      client_secret: '123456',
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    }).then((res) => {
      setToken(res)
      const config = response.config
      return request({
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${res.access_token}`,
        },
      })
    }).catch((error) => {
      // reLogin()
      console.log('refresh error', error)
      return Promise.reject(error)
    })
  }
}

const service = new Request().instance
const fileService = new Request(true).instance

export function request<T = any>(config: AxiosRequestConfig): Promise<T> {
  return service(config) as Promise<T>
}

export function requestFile<T = any>(config: AxiosRequestConfig): Promise<T> {
  return fileService(config) as Promise<T>
}
